import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import apiClient from "../utils/apiClient";
import axios from "axios";
import { getRealtimeBaseUrl } from "../utils/realtimeBaseUrl";
import { getAcceptLanguage } from "../utils/acceptLanguage";
import { useQuery } from "react-query";
import useFilter from "./useFilter";
import useTripSocket from "./useTripSocket";
import {
  appendPathPoint,
  isActiveTripStatus,
  isCompletedTripStatus,
  normalizePathPoint,
  sortAndDedupeTrail,
} from "../utils/tripTracking";

const PATH_POLL_MS = 2000;

const useTrips = ({ tripId: initialTripId } = {}) => {
  const { page, status, type, date, driver_id, search } = useFilter();
  const tripIdRef = useRef(initialTripId);
  const liveTrailRef = useRef([]);
  const lastSocketIndexRef = useRef(0);

  useEffect(() => {
    tripIdRef.current = initialTripId;
  }, [initialTripId]);

  const [driverTrail, setDriverTrail] = useState([]);
  const [driverLocation, setDriverLocation] = useState(null);

  const applyDriverTrail = useCallback((nextTrail) => {
    liveTrailRef.current = nextTrail;
    setDriverTrail(nextTrail);

    const last = nextTrail[nextTrail.length - 1];
    if (last) {
      setDriverLocation({
        lat: last.lat,
        lng: last.lng,
        datetime: last.datetime,
      });
    }
  }, []);

  const syncApiPathToTrail = useCallback((apiPoints) => {
    const normalized = sortAndDedupeTrail(
      (apiPoints || []).map(normalizePathPoint).filter(Boolean),
    );

    if (!normalized.length) {
      applyDriverTrail([]);
      return;
    }

    const lastApi = normalized[normalized.length - 1];
    const lastApiTime = new Date(lastApi.datetime).getTime();
    const apiKeys = new Set(
      normalized.map((p) => `${p.lat.toFixed(5)}_${p.lng.toFixed(5)}`),
    );

    const socketTail = liveTrailRef.current.filter((p) => {
      const pTime = new Date(p.datetime).getTime();
      if (pTime > lastApiTime) return true;
      const key = `${p.lat.toFixed(5)}_${p.lng.toFixed(5)}`;
      return pTime === lastApiTime && !apiKeys.has(key);
    });

    applyDriverTrail(sortAndDedupeTrail([...normalized, ...socketTail]));
  }, [applyDriverTrail]);

  const resyncPathWithServer = useCallback(async () => {
    const tripId = tripIdRef.current;
    if (!tripId) return;

    const token = sessionStorage.getItem("ecoToken");
    if (!token) return;

    try {
      const res = await axios.get(
        `${getRealtimeBaseUrl()}/TripTracking/GetTripPathPoint/${tripId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": getAcceptLanguage(),
          },
        },
      );
      const points = res.data?.data?.points ?? [];
      syncApiPathToTrail(points);
    } catch (err) {
      console.warn("Path resync failed:", err?.message);
    }
  }, [syncApiPathToTrail]);

  const {
    isConnected: isSignalRConnected,
    isConnecting: isSignalRConnecting,
    error: signalRError,
    connect: connectToSignalR,
    disconnect: disconnectFromSignalR,
    tripUpdates: socketTripUpdates,
    lastUpdate,
    getConnectionInfo,
  } = useTripSocket({ onReconnected: resyncPathWithServer });

  const { data, error, isError, isLoading } = useQuery({
    queryKey: ["trips", page, status, type, date, driver_id, search],
    queryFn: async () => {
      const params = {
        ...(status && { status }),
        ...(type !== undefined && type !== null && { type }),
        ...(date !== undefined && date !== null && { date }),
        ...(search !== undefined && search !== null && { search }),
        ...(page && { page }),
        ...(driver_id && { driver_id }),
      };

      const res = await apiClient.get("/trip-infos/", { params });
      return res.data;
    },
  });

  const {
    data: tripDetails,
    error: tripDetailsError,
    isError: tripDetailsIsError,
    isLoading: tripDetailsLoading,
    refetch: refetchTripDetails,
  } = useQuery({
    queryKey: ["tripDetails", initialTripId],
    queryFn: async () => {
      const res = await apiClient.get(`/trips/${initialTripId}`);
      return res.data;
    },
    enabled: !!initialTripId,
    refetchOnWindowFocus: false,
    // react-query v3 passes query data to refetchInterval, not a Query observer
    refetchInterval: (data) => {
      const status = data?.data?.info?.status;
      return isActiveTripStatus(status) ? 5000 : false;
    },
  });

  const tripStatus = tripDetails?.data?.info?.status;
  const isLiveTrip = isActiveTripStatus(tripStatus);
  const isCompletedTrip = isCompletedTripStatus(tripStatus);

  const {
    data: tripPathPoints,
    error: tripPathPointsError,
    isError: tripPathPointsIsError,
    isLoading: tripPathPointsLoading,
    refetch: refetchTripPathPoints,
  } = useQuery({
    queryKey: ["tripPathPoints", initialTripId],
    queryFn: async () => {
      const token = sessionStorage.getItem("ecoToken");
      const res = await axios.get(
        `${getRealtimeBaseUrl()}/TripTracking/GetTripPathPoint/${initialTripId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": getAcceptLanguage(),
          },
        },
      );
      return res.data;
    },
    enabled: !!initialTripId,
    refetchInterval: isLiveTrip ? PATH_POLL_MS : false,
    refetchIntervalInBackground: true,
    // Keep driver trail available after trip ends
    staleTime: isCompletedTrip ? Infinity : 0,
  });

  useEffect(() => {
    if (!tripPathPoints?.data?.points) return;
    syncApiPathToTrail(tripPathPoints.data.points);
  }, [tripPathPoints, syncApiPathToTrail]);

  useEffect(() => {
    if (isCompletedTrip && initialTripId) {
      refetchTripPathPoints();
    }
  }, [isCompletedTrip, initialTripId, refetchTripPathPoints]);

  useEffect(() => {
    if (!isLiveTrip) return;

    const fromIndex = lastSocketIndexRef.current;
    if (socketTripUpdates.length <= fromIndex) return;

    const newUpdates = socketTripUpdates
      .slice(fromIndex)
      .filter(
        (u) =>
          u.latitude != null && u.longitude != null && !u.arrivedTripPointId,
      );

    lastSocketIndexRef.current = socketTripUpdates.length;
    if (newUpdates.length === 0) return;

    let trail = liveTrailRef.current;
    for (const update of newUpdates) {
      trail = appendPathPoint(trail, update);
    }
    applyDriverTrail(trail);
  }, [socketTripUpdates, isLiveTrip, applyDriverTrail]);

  useEffect(() => {
    const currentTripId = tripIdRef.current;

    if (currentTripId && isLiveTrip) {
      connectToSignalR(currentTripId);
      return;
    }

    disconnectFromSignalR();
  }, [isLiveTrip, connectToSignalR, disconnectFromSignalR]);

  useEffect(() => {
    if (!initialTripId) return;

    liveTrailRef.current = [];
    lastSocketIndexRef.current = 0;
    setDriverTrail([]);
    setDriverLocation(null);
  }, [initialTripId]);

  const latestRealTimePoint = useMemo(() => {
    if (!driverLocation) return null;
    return {
      lat: driverLocation.lat,
      lng: driverLocation.lng,
      latitude: driverLocation.lat,
      longitude: driverLocation.lng,
      datetime: driverLocation.datetime,
    };
  }, [driverLocation]);

  const realTimeTrackingPoints = useMemo(
    () =>
      driverTrail.map((p, index) => ({
        id: `trail-${index}`,
        lat: p.lat,
        lng: p.lng,
        latitude: p.lat,
        longitude: p.lng,
        datetime: p.datetime,
        isTrackingPoint: true,
      })),
    [driverTrail],
  );

  const testSignalRConnection = useCallback(async () => {
    const currentTripId = tripIdRef.current;
    if (currentTripId) {
      return connectToSignalR(currentTripId);
    }
    return false;
  }, [connectToSignalR]);

  return {
    isSignalRConnected,
    isSignalRConnecting,
    signalRError,
    disconnectFromSignalR,
    connectToSignalR: () => {
      const currentTripId = tripIdRef.current;
      if (currentTripId) {
        return connectToSignalR(currentTripId);
      }
      return Promise.resolve(false);
    },
    testSignalRConnection,
    getSignalRConnectionInfo: getConnectionInfo,

    driverTrail,
    driverLocation,
    isLiveTrip,
    isCompletedTrip,
    tripStatus,

    realTimeTrackingPoints,
    latestRealTimePoint,
    socketTripUpdates,
    resyncPathWithServer,

    tripPathPointsError,
    tripPathPointsIsError,
    tripPathPointsLoading,
    refetchTripPathPoints,

    data,
    error,
    isError,
    isLoading,
    tripDetails,
    tripDetailsError,
    tripDetailsIsError,
    tripDetailsLoading,
    refetchTripDetails,
    lastUpdate,
    tripPathPoints,
  };
};

export default useTrips;
